import { clsx } from '@alexis/helpers/clsx';
import { genzoTrackActionEvent } from '@alexis/helpers/genzo';
import { translateNumber, translateText } from '@alexis/helpers/translation';
import { capitalize } from 'lodash-es';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import Dialog from '@components/Dialog';
import HotelAmenityIcon from '@components/hotels/HotelAmenityIcon';
import Image from '@components/Image';
import ImageGallery from '@components/ImageGallery/ImageGallery';
import Price from '@components/Price';

import { injectGoogleMapScript } from '@helpers/googleMap';
import {
  cloudflareImageTransformations,
  cloudinaryImageTransformations,
  isCloudinarySrc,
} from '@helpers/imageTransformations';
import { convertHotelRatePriceToPrice, getBestDealUsualPrice } from '@helpers/price';
import {
  getReviewRatingTypeColorClassName,
  getReviewRatingTypeDisplay,
  getReviewScoreDisplay,
} from '@helpers/review';

import Chevron from '@icons/chevron-left-medium.svg';
import MultipleImages from '@icons/multiple_images.svg';
import OpenInNewIcon from '@icons/open_in_new.svg';
import SentimentDissatisfiedIcon from '@icons/sentiment_dissatisfied.svg';
import SentimentNeutralIcon from '@icons/sentiment_neutral.svg';
import SentimentSatisfied from '@icons/sentiment_satisfied.svg';
import CloseIcon from '@icons/x-close-small.svg';

import { useIntersectionObserver } from '@hooks/useIntersectionObserver';
import { useViewportSize } from '@hooks/useViewportSize';

import {
  getCurrencyState,
  getExchangeRateState,
  getLocaleState,
  getPriceOptionsState,
  getTranslationsState,
  getUserState,
} from '@redux/selectors';

import { Currency } from '@wegoTypes/currency';
import { HotelBookingRoomTypes } from '@wegoTypes/hotelBooking/hotelRoomTypes';
import { Hotel } from '@wegoTypes/hotels/hotel';
import { HotelReview } from '@wegoTypes/hotels/hotelReview';
import { MetasearchHotelRate } from '@wegoTypes/hotels/metasearch/metasearchHotelRate';
import { Translations } from '@wegoTypes/translations';
import { User } from '@wegoTypes/user';

import styles from './HotelInformation.module.scss';

interface HotelInformationProps {
  pageViewId: string;
  hotel: Hotel | undefined;
  allReviewerGroupReview: HotelReview | undefined;
  bestDeal?: MetasearchHotelRate;
  hotelBookingRoomTypes?: HotelBookingRoomTypes;
  wegoAnalyticsClientId: string | undefined;
  wegoAnalyticsClientSessionId: string | undefined;
  isOpenMapPopup: boolean;
  setIsOpenMapPopup: (isOpen: boolean) => void;
  scrollDownSmoothly: (id: string) => void;
  rateHandoffHref: (rate: MetasearchHotelRate) => string;
  handleRateClick: (rate: MetasearchHotelRate) => void;
  handleHotelDealsRateClick: (rate: MetasearchHotelRate) => void;
}

const HotelInformation: React.FC<HotelInformationProps> = ({
  pageViewId,
  hotel,
  allReviewerGroupReview,
  bestDeal,
  hotelBookingRoomTypes,
  wegoAnalyticsClientId,
  wegoAnalyticsClientSessionId,
  isOpenMapPopup,
  setIsOpenMapPopup,
  scrollDownSmoothly,
  rateHandoffHref,
  handleRateClick,
  handleHotelDealsRateClick,
}) => {
  const apiBaseUrl: string = API_BASE_URL;
  const currency: Currency = useSelector(getCurrencyState);
  const exchangeRate: ExchangeRate = useSelector(getExchangeRateState);
  const locale: string = useSelector(getLocaleState);
  const priceOptions: PriceOptions = useSelector(getPriceOptionsState);
  const translations: Translations = useSelector(getTranslationsState);
  const user: User | null = useSelector(getUserState);

  const [showMoreImages, setShowMoreImages] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);

  const { width } = useViewportSize();
  const [mapDivRef, isMapDivVisible] = useIntersectionObserver<HTMLDivElement>();
  const popupMapRef = useRef<HTMLDivElement>(null);

  const noImagePlaceholderSrc = cloudinaryImageTransformations(
    'https://assets.wego.com/image/upload/v1639463186/web/misc/hotel-image-placeholder.png',
    'h_376',
    'c_fill',
    'f_auto',
    'fl_lossy',
    'q_auto:low',
    'g_auto',
  );

  const bestDealUsualPrice = getBestDealUsualPrice(bestDeal, priceOptions);

  const handleOpenGallery = useCallback<(startIndex: number) => void>(
    (startIndex: number) => {
      if (!!wegoAnalyticsClientId && !!wegoAnalyticsClientSessionId) {
        const eventData = {
          id: pageViewId,
          category: 'hotel_details',
          object: 'image',
          action: 'click_image',
          value: `stretched_image_click_${startIndex}`,
        };
        genzoTrackActionEvent(
          apiBaseUrl,
          wegoAnalyticsClientId,
          wegoAnalyticsClientSessionId,
          user?.userHash,
          eventData,
        );
      }

      setCurrentImageIndex(startIndex);
      setShowMoreImages(true);
    },
    [wegoAnalyticsClientId, wegoAnalyticsClientSessionId, apiBaseUrl, pageViewId, user],
  );

  const getImageTransformationsUrl = useCallback<(url: string, isThumbnail?: boolean) => string>(
    (url: string, isThumbnail = true) => {
      if (!url) {
        return noImagePlaceholderSrc;
      }
      if (isCloudinarySrc(url)) {
        return cloudinaryImageTransformations(
          url,
          `h_${isThumbnail ? '185' : '376'}`,
          'c_fill',
          'f_auto',
          'fl_lossy',
          'q_auto:best',
          'g_auto',
        );
      } else {
        return cloudflareImageTransformations(url, {
          format: 'auto',
          height: isThumbnail ? 185 : 376,
          quality: 90,
        });
      }
    },
    [],
  );

  const displayedImages = useMemo(() => {
    if (!!hotel?.images && hotel?.images.length > 0) {
      if (hotel?.images.length > 7 && width >= 1280) {
        return hotel?.images.slice(0, 7);
      } else if (hotel?.images.length > 4 && width < 1280 && width >= 1024) {
        return hotel?.images.slice(0, 4);
      } else if (hotel?.images.length > 3 && width < 1024) {
        return hotel?.images.slice(0, 3);
      }
      // Fill empty image placeholders
      return hotel?.images.concat(
        Array(7 - hotel?.images.length).fill({ url: noImagePlaceholderSrc }),
      );
    }
    return null;
  }, [hotel?.images, width]);

  const imageCount = (hotel?.images?.length || 0) - (displayedImages?.length || 0);

  // Initialize google map
  useEffect(() => {
    if (isMapDivVisible && !!hotel?.latitude && !!hotel?.longitude) {
      const initializeGoogleMap = async (locale: string): Promise<void> => {
        await injectGoogleMapScript(locale);

        const googleMapLatLngLiteral: google.maps.LatLngLiteral = {
          lat: hotel.latitude,
          lng: hotel.longitude,
        };

        const mapOptions: google.maps.MapOptions = {
          center: googleMapLatLngLiteral,
          keyboardShortcuts: false,
          mapTypeControl: false,
          zoom: 17,
          zoomControl: false,
          fullscreenControl: false,
          clickableIcons: false,
          streetViewControl: false,
        };

        const map = new google.maps.Map(mapDivRef.current!, mapOptions);
        new google.maps.Marker({ position: googleMapLatLngLiteral, map });

        if (popupMapRef.current) {
          const popupMap = new google.maps.Map(popupMapRef.current, mapOptions);
          new google.maps.Marker({ position: googleMapLatLngLiteral, map: popupMap });
        }
      };

      initializeGoogleMap(locale);
    }
  }, [isMapDivVisible, hotel]);

  return (
    <div className={styles.container}>
      {/* Hotel Image Carousel */}
      {displayedImages?.length && !!hotel?.images && hotel?.images?.length > 3 ? (
        <div className={styles.imageFlexBox}>
          <div className={styles.mainImageWrapper}>
            <div className={styles.mainImage}>
              <Image
                className={styles.hotelImageThumbnail}
                noImagePlaceholderSrc={noImagePlaceholderSrc}
                placeholderSrc={noImagePlaceholderSrc}
                src={getImageTransformationsUrl(displayedImages[0].url, false)}
                onClick={() => handleOpenGallery(0)}
              />
              {!!bestDeal ? (
                <div className={styles.priceAndProvider}>
                  <div className={styles.bestDeal}>
                    <div>
                      <Price
                        className={styles.price}
                        currency={currency}
                        exchangeRate={exchangeRate}
                        isInline
                        locale={locale}
                        price={convertHotelRatePriceToPrice(
                          bestDeal.price,
                          priceOptions.showTotalPrice,
                          priceOptions.isIncludingTaxOrFee,
                        )}
                      />
                      {!!bestDeal.usualPrice && bestDealUsualPrice ? (
                        <div className={styles.discounted}>
                          <div className={styles.discountedPercentageV2}>{`${Math.round(
                            bestDeal.usualPrice.discountToUsualAmount * 100,
                          )}% off`}</div>
                          <Price
                            isInline
                            isStrikethrough
                            className={styles.usualPrice}
                            price={bestDealUsualPrice}
                            locale={locale}
                            currency={currency}
                            exchangeRate={exchangeRate}
                          />
                        </div>
                      ) : null}
                    </div>
                    <a
                      className={styles.provider}
                      href={rateHandoffHref(bestDeal)}
                      target='_blank'
                      rel='noreferrer'
                    >
                      {bestDeal.provider.name}
                    </a>
                  </div>

                  {!bestDeal.provider!.directBooking ? (
                    <a
                      className={clsx(styles.viewDeal)}
                      href={rateHandoffHref(bestDeal)}
                      onClick={() => handleHotelDealsRateClick(bestDeal)}
                      target='_blank'
                      rel='noreferrer'
                    >
                      {translations.view_deal}
                      <OpenInNewIcon className={styles.openIcon} />
                    </a>
                  ) : (
                    <div className={styles.viewDeal} onClick={() => handleRateClick(bestDeal)}>
                      {hotelBookingRoomTypes?.data.rooms.find(
                        (room) => bestDeal.params.room_type_id === room.typeId,
                      ) ? (
                        <span>{translations.Select} </span>
                      ) : (
                        translations.book_now
                      )}
                      <OpenInNewIcon className={styles.openIcon} />
                    </div>
                  )}
                </div>
              ) : null}
            </div>

            <div className={styles.horizontalImages}>
              {displayedImages.slice(1, 4).map((image, index) => (
                <div
                  key={image.url}
                  className={styles.wrapper}
                  onClick={() => handleOpenGallery(index + 1)}
                >
                  <Image
                    className={styles.hotelSideImage}
                    noImagePlaceholderSrc={noImagePlaceholderSrc}
                    placeholderSrc={noImagePlaceholderSrc}
                    src={getImageTransformationsUrl(image?.url)}
                  />
                  {index + 1 === displayedImages.length - 1 &&
                  displayedImages.length < 6 &&
                  imageCount > 3 ? (
                    <>
                      <div className={clsx(styles.overlay, styles.radiusSideBot)}></div>
                      <div className={styles.icon}>
                        <MultipleImages />
                        <p>+{imageCount}</p>
                      </div>
                    </>
                  ) : null}
                </div>
              ))}
            </div>
          </div>

          <div className={clsx(styles.imageFlexBox, styles.imageColumn)}>
            {displayedImages.length > 5
              ? displayedImages
                  .slice(4, 6)
                  .map((image, index) => (
                    <Image
                      key={image.url}
                      className={clsx(styles.hotelSideImage)}
                      noImagePlaceholderSrc={noImagePlaceholderSrc}
                      placeholderSrc={noImagePlaceholderSrc}
                      src={getImageTransformationsUrl(image?.url)}
                      onClick={() => handleOpenGallery(index + 4)}
                    />
                  ))
              : null}

            <div className={clsx(styles.galleryWrapper)} onClick={() => handleOpenGallery(6)}>
              <Image
                className={clsx(styles.hotelSideImage, styles.radiusSideBot)}
                noImagePlaceholderSrc={noImagePlaceholderSrc}
                placeholderSrc={noImagePlaceholderSrc}
                src={getImageTransformationsUrl(displayedImages[6]?.url)}
              />
              {imageCount > 6 ? (
                <>
                  <div className={clsx(styles.overlay, styles.radiusSideBot)}></div>
                  <div className={styles.icon}>
                    <MultipleImages />
                    <p>+{imageCount}</p>
                  </div>
                </>
              ) : null}
            </div>

            {showMoreImages ? (
              <ImageGallery
                isOpen={showMoreImages}
                startImageIndex={currentImageIndex}
                imageUrls={hotel?.images?.map((image) => image.url)}
                locale={locale}
                onClose={() => {
                  setShowMoreImages(false);
                }}
              />
            ) : null}
          </div>
        </div>
      ) : (
        <div className={styles.imageFlexBox}>
          <img className={styles.emptyImage} src={noImagePlaceholderSrc} />
          <div className={clsx(styles.imageFlexBox, styles.imageColumn)}>
            <img
              className={clsx(styles.hotelSideImage, styles.radiusSideTop)}
              src={noImagePlaceholderSrc}
            />
            <img className={styles.hotelSideImage} src={noImagePlaceholderSrc} />
            <img
              className={clsx(styles.hotelSideImage, styles.radiusSideBot)}
              src={noImagePlaceholderSrc}
            />
          </div>
        </div>
      )}

      <div className={styles.ratingAmenitiesWrapper}>
        {/* All Review */}
        {!!allReviewerGroupReview ? (
          <>
            <div className={styles.allReview}>
              <div
                className={clsx(
                  styles.score,
                  getReviewRatingTypeColorClassName(allReviewerGroupReview.score, styles),
                )}
              >
                {translateNumber(
                  getReviewScoreDisplay(allReviewerGroupReview.score),
                  locale === 'fa',
                )}
              </div>

              <div
                className={clsx(
                  styles.ratingType,
                  getReviewRatingTypeColorClassName(allReviewerGroupReview.score, styles),
                )}
              >
                {getReviewRatingTypeDisplay(allReviewerGroupReview.score, translations)}
              </div>

              <div className={styles.reviewCount}>
                {translateText(translations.count_review, locale, allReviewerGroupReview.count)}
              </div>
            </div>

            {/* Good to know Review */}
            {hotel?.aiReviews?.length ? (
              <>
                <div className={styles.goodToKnowSnippetWrapper}>
                  {hotel.aiReviews.slice(0, 2).map((goodToKnowSnippet) => (
                    <div
                      key={goodToKnowSnippet.id}
                      className={clsx(
                        styles.goodToKnowSnippet,
                        !!goodToKnowSnippet.sentiment &&
                          styles[goodToKnowSnippet.sentiment.toLowerCase()],
                      )}
                    >
                      {goodToKnowSnippet.sentiment?.toUpperCase() === 'POSITIVE' ? (
                        <SentimentSatisfied className={styles.icon} />
                      ) : null}
                      {goodToKnowSnippet.sentiment?.toUpperCase() === 'NEUTRAL' ? (
                        <SentimentNeutralIcon className={styles.icon} />
                      ) : null}
                      {goodToKnowSnippet.sentiment?.toUpperCase() === 'NEGATIVE' ? (
                        <SentimentDissatisfiedIcon className={styles.icon} />
                      ) : null}

                      <div className={styles.text}>{goodToKnowSnippet.text}</div>
                    </div>
                  ))}
                </div>

                <div className={styles.viewMore} onClick={() => scrollDownSmoothly('GuestRating')}>
                  <p>{translations.view_more_ratings}</p>
                  <Chevron className={clsx(styles.chevron)} />
                </div>
              </>
            ) : (
              <div>{translations.no_reviews_info}</div>
            )}
          </>
        ) : (
          <>
            <div className={styles.noReviews}>
              <div className={styles.dots}>...</div>
              {translations.pending_reviews}
            </div>
            <div>{translations.no_reviews_info}</div>
          </>
        )}

        <div className={styles.amenitiesSectionWrapper}>
          <p className={styles.popularAmenities}>{translations.popular_amenities}</p>

          {!!hotel?.amenities && hotel.amenities.length > 0 ? (
            <>
              <div className={styles.amenitiesList}>
                {hotel.amenities.slice(0, 2).map((amenity) => {
                  return (
                    <div key={amenity.id} className={styles.amenity}>
                      <HotelAmenityIcon
                        amenityId={amenity.id}
                        className={styles.hotelAmenitiesIcon}
                      />
                      {amenity.name}
                    </div>
                  );
                })}
              </div>
              <div className={styles.viewMore} onClick={() => scrollDownSmoothly('Details')}>
                <p>{translations.view_more_amenities}</p>
                <Chevron className={clsx(styles.chevron)} />
              </div>
            </>
          ) : (
            <div>{translations.no_amenities_info}</div>
          )}
        </div>

        <div ref={mapDivRef} className={styles.hotelMap} onClick={() => setIsOpenMapPopup(true)} />

        <Dialog
          className={styles.mapDialog}
          isOpen={isOpenMapPopup}
          onClose={() => setIsOpenMapPopup(false)}
        >
          <div className={styles.closeMapButton} onClick={() => setIsOpenMapPopup(false)}>
            {capitalize(translations.close_map as string)}
            <CloseIcon />
          </div>

          <div ref={popupMapRef} className={styles.mapPopup} />
        </Dialog>
      </div>
    </div>
  );
};

export default HotelInformation;
